import type { CheckResult } from '@domaintriage/shared';

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------

/**
 * Format check results as a Markdown table.
 *
 * Columns: Domain | Status | Checked At | Notes
 */
export function toMarkdownTable(
  results: CheckResult[],
  notes?: Map<string, string>,
): string {
  const lines: string[] = [];

  lines.push('| Domain | Status | Checked At | Notes |');
  lines.push('| ------ | ------ | ---------- | ----- |');

  for (const r of results) {
    const note = notes?.get(r.domain) ?? '';
    // Pipe characters inside cell content would break the table.
    const safeNote = note.replace(/\|/g, '\\|');
    const safeDomain = r.domain.replace(/\|/g, '\\|');
    lines.push(
      `| ${safeDomain} | ${r.status} | ${r.checkedAt} | ${safeNote} |`,
    );
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * Escape a value for inclusion in a CSV cell per RFC 4180.
 *
 * If the value contains a comma, double-quote, or newline it is wrapped in
 * double-quotes, with internal double-quotes doubled.
 */
function csvEscape(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format check results as CSV.
 *
 * Columns: Domain, Status, Checked At, Notes
 */
export function toCsv(
  results: CheckResult[],
  notes?: Map<string, string>,
): string {
  const lines: string[] = [];

  lines.push(
    [
      csvEscape('Domain'),
      csvEscape('Status'),
      csvEscape('Checked At'),
      csvEscape('Notes'),
    ].join(','),
  );

  for (const r of results) {
    const note = notes?.get(r.domain) ?? '';
    lines.push(
      [
        csvEscape(r.domain),
        csvEscape(r.status),
        csvEscape(r.checkedAt),
        csvEscape(note),
      ].join(','),
    );
  }

  // Trailing newline for POSIX friendliness.
  return lines.join('\n') + '\n';
}

// ---------------------------------------------------------------------------
// Download helper
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download for arbitrary string content.
 *
 * Creates a temporary `<a>` with a Blob URL, clicks it, and cleans up.
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();

  // Clean up after the browser has had a chance to start the download.
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  }, 100);
}
